// Exported so the tests assert against this window rather than a copy of it.
// Harmless in the browser: sample-toast.liquid loads this with type='module'.
export const AUTO_DISMISS_MS = 4000;
const GENERIC_ERROR_MESSAGE = 'Something went wrong. Please try again.';

// Writes to the existing #status-announcement live region (theme.liquid) rather
// than putting aria-live on the toast itself — mirrors theme.js's announceStatus,
// reimplemented locally per docs/sample-add-toast-plan.md (no import from 'theme').
let pendingAnnounceTimer = null;

function announceStatus(message) {
    const holder = document.getElementById('status-announcement');
    if (!holder) return;
    clearTimeout(pendingAnnounceTimer);
    holder.textContent = '';
    pendingAnnounceTimer = setTimeout(() => {
        holder.textContent = message;
    }, 100);
}

function cancelPendingAnnounce() {
    clearTimeout(pendingAnnounceTimer);
    pendingAnnounceTimer = null;
}

// <sample-toast> is rendered once in theme.liquid and knows nothing about forms
// or buttons — only sample:added / sample:add-failed (dispatched by every
// <sample-add> instance) and dialog:after-show (dispatched by DialogElement,
// theme.js) for the Cart drawer. That seam is what lets the Shop Samples grid
// opt in with a single dispatchEvent despite using a different add path.
class SampleToast extends HTMLElement {
    connectedCallback() {
        this._messageEl = this.querySelector('[data-sample-toast-message]');
        this._defaultMessage =
            (this._messageEl && this._messageEl.dataset.defaultMessage) ||
            (this._messageEl && this._messageEl.textContent) ||
            '';
        this._pauseReasons = new Set();
        this._remaining = AUTO_DISMISS_MS;
        this._startedAt = null;
        this._timer = null;

        this._onSampleAdded = this._onSampleAdded.bind(this);
        this._onSampleAddFailed = this._onSampleAddFailed.bind(this);
        this._onDialogAfterShow = this._onDialogAfterShow.bind(this);
        this._onMouseEnter = () => this._pause('hover');
        this._onMouseLeave = () => this._resume('hover');
        this._onFocusIn = () => this._pause('focus');
        this._onFocusOut = () => this._resume('focus');
        this._onKeydown = this._onKeydown.bind(this);
        this._onClick = this._onClick.bind(this);

        document.addEventListener('sample:added', this._onSampleAdded);
        document.addEventListener('sample:add-failed', this._onSampleAddFailed);
        document.addEventListener('dialog:after-show', this._onDialogAfterShow);
        this.addEventListener('mouseenter', this._onMouseEnter);
        this.addEventListener('mouseleave', this._onMouseLeave);
        this.addEventListener('focusin', this._onFocusIn);
        this.addEventListener('focusout', this._onFocusOut);
        this.addEventListener('keydown', this._onKeydown);
        this.addEventListener('click', this._onClick);
    }

    disconnectedCallback() {
        clearTimeout(this._timer);
        // The deferred write lands 100ms after announceStatus; without this a toast
        // removed inside that window still narrates its message.
        cancelPendingAnnounce();
        document.removeEventListener('sample:added', this._onSampleAdded);
        document.removeEventListener('sample:add-failed', this._onSampleAddFailed);
        document.removeEventListener('dialog:after-show', this._onDialogAfterShow);
        this.removeEventListener('mouseenter', this._onMouseEnter);
        this.removeEventListener('mouseleave', this._onMouseLeave);
        this.removeEventListener('focusin', this._onFocusIn);
        this.removeEventListener('focusout', this._onFocusOut);
        this.removeEventListener('keydown', this._onKeydown);
        this.removeEventListener('click', this._onClick);
    }

    get _reducedMotion() {
        const settings = window.themeVariables && window.themeVariables.settings;
        return (
            window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
            Boolean(settings && settings.reduceDrawerAnimation)
        );
    }

    _onSampleAdded() {
        this._show(this._defaultMessage, { isError: false });
    }

    _onSampleAddFailed(event) {
        const error = (event.detail && event.detail.error) || GENERIC_ERROR_MESSAGE;
        this._show(error, { isError: true });
    }

    _onDialogAfterShow(event) {
        if (event.target && event.target.id === 'cart-drawer') {
            this._dismiss();
        }
    }

    _onKeydown(event) {
        if (event.key === 'Escape') {
            this._dismiss();
        }
    }

    _onClick(event) {
        if (event.target.closest('[data-sample-toast-close]')) {
            this._dismiss();
        }
    }

    _show(message, { isError }) {
        this.classList.toggle('sample-toast--error', Boolean(isError));
        const successIcon = this.querySelector('[data-sample-toast-icon-success]');
        const errorIcon = this.querySelector('[data-sample-toast-icon-error]');
        if (successIcon) successIcon.hidden = Boolean(isError);
        if (errorIcon) errorIcon.hidden = !isError;
        if (this._messageEl) this._messageEl.textContent = message;

        // Unhiding flips `display: none` back on, which restarts any animation still
        // attached — so a leftover --enter class from an earlier show would replay the
        // slide-in even under reduced motion. Reduced motion can change between shows
        // (the media query is live), so clear the class before unhiding rather than
        // trusting that it was never added.
        if (this._reducedMotion) {
            this.classList.remove('sample-toast--enter');
        }

        this.hidden = false;
        this._pauseReasons.clear();
        this._remaining = AUTO_DISMISS_MS;
        // _startTimer is no longer guaranteed to run below, so the previous show's
        // timer has to be dropped here rather than inside it.
        clearTimeout(this._timer);

        // A repeat add re-shows a toast the pointer may already be resting on (or that
        // still holds focus). No fresh mouseenter/focusin fires in that case, so the
        // cleared reasons have to be re-derived from the live DOM — otherwise the toast
        // resumes counting down and dismisses itself under the cursor.
        if (this.matches(':hover')) this._pauseReasons.add('hover');
        if (this.contains(document.activeElement)) this._pauseReasons.add('focus');

        if (!this._reducedMotion) {
            this._playEntrance();
        }

        announceStatus(message);
        if (this._pauseReasons.size === 0) {
            this._startTimer();
        }
    }

    // Re-triggering a CSS animation needs a forced reflow, otherwise toggling the
    // same class off/on in the same tick is a no-op and the animation never
    // restarts on a repeat add.
    _playEntrance() {
        this.classList.remove('sample-toast--enter');
        void this.offsetWidth;
        this.classList.add('sample-toast--enter');
    }

    _startTimer() {
        clearTimeout(this._timer);
        this._startedAt = Date.now();
        this._timer = setTimeout(() => this._dismiss(), this._remaining);
    }

    _pause(reason) {
        if (this.hidden) return;
        this._pauseReasons.add(reason);
        if (this._pauseReasons.size !== 1) return;

        clearTimeout(this._timer);
        this._remaining = Math.max(0, this._remaining - (Date.now() - this._startedAt));
    }

    _resume(reason) {
        this._pauseReasons.delete(reason);
        if (this._pauseReasons.size === 0 && !this.hidden) {
            this._startTimer();
        }
    }

    _dismiss() {
        clearTimeout(this._timer);
        this._pauseReasons.clear();
        this.hidden = true;
    }
}

if (!window.customElements.get('sample-toast')) {
    window.customElements.define('sample-toast', SampleToast);
}
